import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import { LineChart, Table2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { FilterBuilder } from '@/components/filters/FilterBuilder';
import { DatePresetPicker } from '@/components/filters/DatePresetPicker';
import { MultiSelect } from './MultiSelect';
import { FieldSelect } from './FieldSelect';
import {
  getTableOptions,
  INTERVAL_OPTIONS,
  COUNT_OPTIONS,
  CALC_OPTIONS,
} from './product.constants';
import type { ProductState } from './useProductState';
import type { DateRange } from '@/lib/dates';

interface ProductConfigPanelProps {
  state: ProductState;
  onChange: (patch: Partial<ProductState>) => void;
  dates: DateRange;
  /** Events contributed by active custom-field formulas (union of all formula events). */
  formulaEvents?: string[];
}

/**
 * Configuration panel for the product report builder.
 *
 * Layout (4-row grid):
 *   Row 1: Table | Events (multi) | Date Preset
 *   Row 2: Chart Type | Interval | Count Method | Aggregation (metric + calc)
 *   Row 3: Custom Formula Metric
 *   Row 4: Breakdown/Pivot config
 *   Row 5: Filters
 */
export function ProductConfigPanel({ state, onChange, dates, formulaEvents = [] }: ProductConfigPanelProps) {
  // Tables — use local constants (static), no API needed
  const tableOptions = getTableOptions();

  // Fetch events for selected table + date range (requires backend)
  const { data: eventsData } = useQuery<{ events: string[] }>({
    queryKey: ['events', state.table, dates.startDate, dates.endDate],
    queryFn: () =>
      api.post<{ events: string[] }>(endpoints.analytics.events(), {
        tableName: state.table,
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    enabled: !!state.table,
    retry: false,
  });

  // Merge user-selected events with formula events for breakdown/metric queries
  const eventsForSchema = useMemo(() => {
    const set = new Set([...state.events, ...formulaEvents]);
    return Array.from(set).sort();
  }, [state.events, formulaEvents]);

  // Fetch event properties for breakdown/column selectors (requires backend)
  const { data: propertiesData } = useQuery<{ properties: string[] }>({
    queryKey: ['properties', state.table, eventsForSchema, dates.startDate, dates.endDate],
    queryFn: () =>
      api.post<{ properties: string[] }>(endpoints.analytics.properties(), {
        tableName: state.table,
        eventNames: eventsForSchema,
        startDate: dates.startDate,
        endDate: dates.endDate,
      }),
    enabled: !!state.table && eventsForSchema.length > 0,
    retry: false,
  });

  // Fetch numeric params for metric dropdown (requires backend)
  const { data: numericData } = useQuery<{ numericParams: Array<{ parameterName: string; label: string; value: string }> }>({
    queryKey: ['numericParams', state.table, eventsForSchema, dates.startDate, dates.endDate],
    queryFn: () =>
      api.post<{ numericParams: Array<{ parameterName: string; label: string; value: string }> }>(
        endpoints.analytics.numericParams(),
        { tableName: state.table, eventNames: eventsForSchema, startDate: dates.startDate, endDate: dates.endDate },
      ),
    enabled: !!state.table && eventsForSchema.length > 0,
    retry: false,
  });

  const eventOptions = (eventsData?.events || []).map((e) => ({ value: e, label: e }));
  const propertyOptions = useMemo(() => {
    const dynamicProps = (propertiesData?.properties || []).map((p) => ({ value: p, label: p }));
    // Standard breakdown fields — always available (resolved server-side)
    const standardFields = [
      { value: 'platform', label: 'platform' },
      { value: 'country', label: 'country' },
      { value: 'app_version', label: 'app_version' },
      { value: 'tier_name', label: 'tier_name' },
    ];
    for (const sf of standardFields) {
      if (!dynamicProps.some((p) => p.value === sf.value)) {
        dynamicProps.push(sf);
      }
    }
    return dynamicProps;
  }, [propertiesData]);

  // Build metric field options from numeric params
  const metricFieldOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [
      { value: '', label: 'None' },
    ];
    for (const p of numericData?.numericParams || []) {
      opts.push({ value: p.value, label: p.value });
    }
    return opts;
  }, [numericData]);

  return (
    <div className="p-5 space-y-5">
      {/* Row 1: Data Source */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Table"
          options={tableOptions}
          value={state.table}
          onChange={(e) =>
            onChange({
              table: e.target.value,
              events: [],
              breakdown: '',
              col: '',
              metric: '',
              calc: '',
              count: 'all',
              filters: '',
            })
          }
          placeholder="Select table..."
        />

        <MultiSelect
          label="Events"
          options={eventOptions}
          value={state.events}
          onChange={(events) =>
            onChange({
              events,
              breakdown: '',
              col: '',
              metric: '',
              calc: '',
            })
          }
          placeholder={!state.table ? 'Select a table first...' : 'Select events...'}
          disabled={!state.table}
        />

        <DatePresetPicker
          value={state.preset}
          onChange={(preset) => onChange({ preset })}
          startDate={state.start}
          endDate={state.end}
          onDateChange={(start, end) => onChange({ start, end, preset: '' })}
          comparison={{
            preset: state.cmpPreset,
            start: state.cmpStart,
            end: state.cmpEnd,
            onPresetChange: (cmpPreset) => onChange({ cmpPreset, cmpStart: '', cmpEnd: '' }),
            onDateChange: (cmpStart, cmpEnd) => onChange({ cmpStart, cmpEnd, cmpPreset: '' }),
            onClear: () => onChange({ cmpPreset: '', cmpStart: '', cmpEnd: '' }),
            singleDate: state.chart === 'ts',
          }}
        />
      </div>

      {/* Row 2: View Configuration */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ChartTypePicker
          value={state.chart}
          onChange={(chart) => onChange({ chart })}
        />

        <Select
          label="Interval"
          options={[...INTERVAL_OPTIONS]}
          value={state.interval}
          onChange={(e) => onChange({ interval: e.target.value })}
        />

        <Select
          label="Count Method"
          options={[...COUNT_OPTIONS]}
          value={state.count}
          onChange={(e) => onChange({ count: e.target.value })}
        />

        {state.chart === 'ts' && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Cumulative</label>
            <button
              type="button"
              onClick={() => onChange({ cumulative: !state.cumulative })}
              className={cn(
                'h-9 px-4 rounded-lg border text-sm font-medium transition-colors',
                state.cumulative
                  ? 'bg-brand-50 border-brand-300 text-brand-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              {state.cumulative ? 'On' : 'Off'}
            </button>
          </div>
        )}
      </div>

      {/* Row 3: Aggregation + Custom Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Aggregation</label>
          <div className="flex gap-2">
            <div className="flex-1">
              <select
                value={state.metric}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    onChange({ metric: '', calc: '' });
                  } else {
                    onChange({ metric: val, calc: state.calc || 'sum' });
                  }
                }}
                className={cn(
                  'w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
                  'transition-shadow duration-150',
                  !state.metric && 'text-slate-400',
                )}
              >
                {metricFieldOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value ? opt.label : 'Select metric field...'}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-32 shrink-0">
              <select
                value={state.calc}
                onChange={(e) => onChange({ calc: e.target.value })}
                disabled={!state.metric}
                className={cn(
                  'w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
                  'transition-shadow duration-150',
                  !state.metric && 'opacity-50 cursor-not-allowed',
                )}
              >
                {CALC_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <FieldSelect
          value={state.formulas}
          onChange={(formulas) => onChange({ formulas })}
          availableEvents={eventsData?.events || []}
          tableName={state.table}
          startDate={dates.startDate}
          endDate={dates.endDate}
        />
      </div>

      {/* Row 4: Chart-specific config */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {state.chart === 'ts' ? (
          <Select
            label="Breakdown"
            options={[{ value: '', label: 'No breakdown' }, ...propertyOptions]}
            value={state.breakdown}
            onChange={(e) => onChange({ breakdown: e.target.value })}
          />
        ) : (
          <>
            <Select
              label="Row Dimension"
              options={[{ value: '', label: 'No row dimension' }, ...propertyOptions]}
              value={state.row}
              onChange={(e) => onChange({ row: e.target.value })}
            />
            <Select
              label="Column Breakdown"
              options={[{ value: '', label: 'None' }, ...propertyOptions]}
              value={state.col}
              onChange={(e) => onChange({ col: e.target.value })}
            />
          </>
        )}
      </div>

      {/* Row 5: Filters */}
      <FilterBuilder
        value={state.filters}
        onChange={(filters) => onChange({ filters })}
      />
    </div>
  );
}

// ─── Chart Type Picker (dropdown with icons) ────────────────────────────────

const CHART_TYPES = [
  { value: 'ts' as const, label: 'Time Series', icon: LineChart },
  { value: 'pivot' as const, label: 'Pivot Table', icon: Table2 },
];

function ChartTypePicker({
  value,
  onChange,
}: {
  value: 'ts' | 'pivot';
  onChange: (v: 'ts' | 'pivot') => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = CHART_TYPES.find((t) => t.value === value) || CHART_TYPES[0];
  const Icon = selected.icon;

  return (
    <div className="w-full" ref={ref}>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">Chart Type</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            'w-full h-9 rounded-lg border bg-white pl-3 pr-8 text-sm text-left',
            'flex items-center gap-2 cursor-pointer',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
            open ? 'border-brand-400 ring-2 ring-brand-500/20' : 'border-slate-200 hover:border-slate-300',
          )}
        >
          <Icon className="w-4 h-4 text-brand-500 shrink-0" />
          <span className="truncate text-slate-900">{selected.label}</span>
          <ChevronDown className={cn(
            'absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform duration-150',
            open && 'rotate-180',
          )} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
            <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-hidden">
              {CHART_TYPES.map((ct) => {
                const CtIcon = ct.icon;
                const isActive = ct.value === value;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => {
                      onChange(ct.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                      isActive
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <CtIcon className={cn('w-4 h-4 shrink-0', isActive ? 'text-brand-500' : 'text-slate-400')} />
                    {ct.label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
